/**
 * Vendor Entity Service - Work Order Management
 *
 * Service layer for vendor work order operations including:
 * - Fetching assigned work orders
 * - Updating work order status and progress
 * - Managing time tracking
 * - Handling materials and costs
 * - File uploads and documentation
 */

import {
  VendorWorkOrder,
  WorkOrderStatus,
  WorkOrderPriority,
  WorkOrderType,
  WorkOrderMaterial,
  WorkOrderUpdate,
  WorkOrderPhoto,
  WorkOrderDocument,
  VendorWorkOrderFilters,
} from '@/types/entities/vendor/workOrders';
import { WorkOrder, TimeEntry, Material } from '@/types/entities/vendor/vendor';

export interface CreateWorkOrderUpdateData {
  workOrderId: string;
  updateType: 'status' | 'progress' | 'schedule' | 'cost' | 'scope' | 'other';
  title: string;
  description: string;
  photos?: File[];
  notifyResident?: boolean;
}

export interface UpdateWorkOrderStatusData {
  workOrderId: string;
  status: WorkOrderStatus;
  notes?: string;
  photos?: File[];
  residentSignature?: string;
}

export interface CreateTimeEntryData {
  workOrderId: string;
  startTime: string;
  endTime?: string;
  description: string;
  billable?: boolean;
  rate?: number;
}

export interface UpdateTimeEntryData {
  entryId: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  description?: string;
  billable?: boolean;
  rate?: number;
}

export interface AddMaterialData {
  workOrderId: string;
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unitCost: number;
  supplier?: string;
  partNumber?: string;
}

export interface UploadDocumentData {
  workOrderId: string;
  file: File;
  type: 'invoice' | 'receipt' | 'warranty' | 'permit' | 'inspection' | 'manual' | 'other';
  caption?: string;
}

class VendorWorkOrderService {
  private baseUrl = '/api/vendor/work-orders';
  private useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';
  private isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
  // In-memory cache for mock work orders so status updates persist during session
  private mockWorkOrdersCache: WorkOrder[] | null = null;
  // In-memory cache for mock time entries grouped by workOrderId
  private mockTimeEntriesCache: Record<string, TimeEntry[]> = {};
  // In-memory cache for mock updates grouped by workOrderId
  private mockWorkOrderUpdatesCache: Record<string, WorkOrderUpdate[]> = {};

  // ====== WORK ORDER MANAGEMENT ======

  async getWorkOrders(vendorId: string, filters?: VendorWorkOrderFilters): Promise<WorkOrder[]> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.getMockWorkOrders(vendorId, filters);
    }

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('vendorId', vendorId);

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach((v) => queryParams.append(`${key}[]`, v.toString()));
            } else if (typeof value === 'object') {
              queryParams.append(key, JSON.stringify(value));
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }

      const response = await fetch(`${this.baseUrl}?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch work orders: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching work orders:', error);
      return this.getMockWorkOrders(vendorId, filters);
    }
  }

  async getWorkOrderById(workOrderId: string): Promise<WorkOrder> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.getMockWorkOrderById(workOrderId);
    }

    try {
      const response = await fetch(`${this.baseUrl}/${workOrderId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch work order: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching work order:', error);
      return this.getMockWorkOrderById(workOrderId);
    }
  }

  async updateWorkOrderStatus(data: UpdateWorkOrderStatusData): Promise<WorkOrder> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.mockUpdateWorkOrderStatus(data);
    }

    try {
      const formData = new FormData();
      formData.append('status', data.status);

      if (data.notes) {
        formData.append('notes', data.notes);
      }

      if (data.residentSignature) {
        formData.append('residentSignature', data.residentSignature);
      }

      if (data.photos) {
        data.photos.forEach((photo, index) => {
          formData.append(`photos[${index}]`, photo);
        });
      }

      const response = await fetch(`${this.baseUrl}/${data.workOrderId}/status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to update work order status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating work order status:', error);
      throw error;
    }
  }

  async addWorkOrderUpdate(data: CreateWorkOrderUpdateData): Promise<WorkOrderUpdate> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.mockAddWorkOrderUpdate(data);
    }

    try {
      const formData = new FormData();
      formData.append('updateType', data.updateType);
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('notifyResident', (data.notifyResident || false).toString());

      if (data.photos) {
        data.photos.forEach((photo, index) => {
          formData.append(`photos[${index}]`, photo);
        });
      }

      const response = await fetch(`${this.baseUrl}/${data.workOrderId}/updates`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to add work order update: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding work order update:', error);
      throw error;
    }
  }

  async getWorkOrderUpdates(workOrderId: string): Promise<WorkOrderUpdate[]> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.getMockWorkOrderUpdates(workOrderId);
    }
    try {
      const response = await fetch(`${this.baseUrl}/${workOrderId}/updates`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch work order updates: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching work order updates:', error);
      // For now just return empty if error in non-mock to avoid breaking UI
      return [];
    }
  }

  // ====== TIME TRACKING ======

  async getTimeEntries(workOrderId: string): Promise<TimeEntry[]> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.getMockTimeEntries(workOrderId);
    }

    try {
      const response = await fetch(`${this.baseUrl}/${workOrderId}/time-entries`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch time entries: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching time entries:', error);
      return this.getMockTimeEntries(workOrderId);
    }
  }

  async createTimeEntry(data: CreateTimeEntryData): Promise<TimeEntry> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.mockCreateTimeEntry(data);
    }

    try {
      const response = await fetch(`${this.baseUrl}/${data.workOrderId}/time-entries`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to create time entry: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating time entry:', error);
      throw error;
    }
  }

  async updateTimeEntry(data: UpdateTimeEntryData): Promise<TimeEntry> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.mockUpdateTimeEntry(data);
    }
    try {
      const response = await fetch(`${this.baseUrl}/time-entries/${data.entryId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to update time entry: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating time entry:', error);
      throw error;
    }
  }

  async deleteTimeEntry(entryId: string): Promise<void> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      this.mockDeleteTimeEntry(entryId);
      return;
    }
    try {
      const response = await fetch(`${this.baseUrl}/time-entries/${entryId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete time entry: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting time entry:', error);
      throw error;
    }
  }

  // ====== MATERIALS MANAGEMENT ======

  async addMaterial(data: AddMaterialData): Promise<Material> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.mockAddMaterial(data);
    }

    try {
      const response = await fetch(`${this.baseUrl}/${data.workOrderId}/materials`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to add material: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding material:', error);
      throw error;
    }
  }

  async updateMaterial(materialId: string, updates: Partial<Material>): Promise<Material> {
    try {
      const response = await fetch(`${this.baseUrl}/materials/${materialId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Failed to update material: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating material:', error);
      throw error;
    }
  }

  async deleteMaterial(materialId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete material: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting material:', error);
      throw error;
    }
  }

  // ====== FILE UPLOADS ======

  async uploadPhoto(
    workOrderId: string,
    file: File,
    type: 'before' | 'during' | 'after' | 'issue',
    caption?: string,
  ): Promise<WorkOrderPhoto> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      if (caption) {
        formData.append('caption', caption);
      }

      const response = await fetch(`${this.baseUrl}/${workOrderId}/photos`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload photo: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  async uploadDocument(data: UploadDocumentData): Promise<WorkOrderDocument> {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('type', data.type);
      if (data.caption) {
        formData.append('caption', data.caption);
      }

      const response = await fetch(`${this.baseUrl}/${data.workOrderId}/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload document: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  // ====== MOCK DATA METHODS ======

  private getMockWorkOrders(vendorId: string, filters?: VendorWorkOrderFilters): WorkOrder[] {
    if (!this.mockWorkOrdersCache) {
      // Seed mock data (active + historical) only once per session
      this.mockWorkOrdersCache = [
        {
          id: 'WO001',
          title: 'Emergency HVAC Repair',
          description:
            'Air conditioning unit in Unit 302 has stopped working. Residents report no cold air and strange noises coming from the unit. Urgent repair needed due to heat wave.',
          priority: 'urgent',
          status: 'assigned',
          location: 'Building A - Unit 302',
          requestedBy: {
            name: 'Sarah Johnson',
            unit: '302',
            contact: '(555) 123-4567',
            email: 'sarah.johnson@email.com',
          },
          assignedTo: {
            vendorId: vendorId,
            vendorName: 'Current User',
          },
          scheduledDate: '2025-08-15T14:00:00Z',
          estimatedHours: 3,
          createdAt: '2025-08-15T08:00:00Z',
          updatedAt: '2025-08-15T10:30:00Z',
          category: 'maintenance',
        },
        {
          id: 'WO002',
          title: 'Pool Filter Replacement',
          description: 'Community pool filter needs replacement and system maintenance check.',
          priority: 'medium',
          status: 'in_progress',
          location: 'Community Pool Area',
          requestedBy: {
            name: 'Maintenance Team',
            contact: '(555) 987-6543',
          },
          assignedTo: {
            vendorId: vendorId,
            vendorName: 'Current User',
          },
          scheduledDate: '2025-08-16T09:00:00Z',
          estimatedHours: 2,
          actualHours: 1.5,
          createdAt: '2025-08-14T16:00:00Z',
          updatedAt: '2025-08-15T11:15:00Z',
          category: 'maintenance',
        },
        {
          id: 'WO003',
          title: 'Electrical Outlet Repair',
          description: 'Multiple outlets in gym area are not working properly.',
          priority: 'high',
          status: 'pending',
          location: 'Community Gym',
          requestedBy: {
            name: 'Facility Manager',
            contact: '(555) 456-7890',
          },
          scheduledDate: '2025-08-17T10:00:00Z',
          estimatedHours: 4,
          createdAt: '2025-08-14T12:00:00Z',
          updatedAt: '2025-08-14T12:00:00Z',
          category: 'electrical',
        },
        // Historical (completed/cancelled) to align with WorkOrderHistory mock list
        {
          id: 'WO101',
          title: 'Landscape Maintenance',
          description: 'Weekly landscape maintenance and irrigation check.',
          priority: 'low',
          status: 'cancelled',
          location: 'Front Garden Area',
          requestedBy: {
            name: 'Landscape Coordinator',
            contact: '(555) 789-0123',
            email: 'landscape.coordinator@example.com',
          },
          scheduledDate: '2025-08-01T07:00:00Z',
          estimatedHours: 3,
          actualHours: 0,
          materials: [
            {
              id: 'mat1011',
              name: 'Mulch (bag)',
              quantity: 20,
              unit: 'bag',
              cost: 4.5,
              supplier: 'Garden Supply Co.',
            },
            {
              id: 'mat1012',
              name: 'Fertilizer (40lb)',
              quantity: 2,
              unit: 'bag',
              cost: 25.0,
              supplier: 'AgriSupply',
            },
          ],
          createdAt: '2025-07-30T15:00:00Z',
          updatedAt: '2025-08-01T06:00:00Z',
          notes: 'Cancelled due to weather conditions.',
        },
        {
          id: 'WO102',
          title: 'Elevator Inspection',
          description: 'Quarterly safety inspection of elevator system.',
          priority: 'high',
          status: 'completed',
          location: 'Building A - Elevator',
          requestedBy: {
            name: 'Property Manager',
            contact: '(555) 678-9012',
            email: 'property.manager@example.com',
          },
          scheduledDate: '2025-08-05T10:00:00Z',
          completedDate: '2025-08-05T14:00:00Z',
          estimatedHours: 4,
          actualHours: 4,
          materials: [
            {
              id: 'mat1021',
              name: 'Inspection Certificate',
              quantity: 1,
              unit: 'each',
              cost: 120.0,
              supplier: 'Elevator Services Inc.',
            },
          ],
          createdAt: '2025-08-03T09:00:00Z',
          updatedAt: '2025-08-05T14:00:00Z',
          notes: 'All safety systems operational. Minor adjustment to door sensor.',
        },
        {
          id: 'WO103',
          title: 'Gym Equipment Maintenance',
          description: 'Monthly maintenance check on all gym equipment.',
          priority: 'medium',
          status: 'completed',
          location: 'Community Gym',
          requestedBy: {
            name: 'Facility Manager',
            contact: '(555) 456-7890',
            email: 'facility.manager@example.com',
          },
          scheduledDate: '2025-08-08T08:00:00Z',
          completedDate: '2025-08-08T16:00:00Z',
          estimatedHours: 6,
          actualHours: 7,
          materials: [
            {
              id: 'mat1031',
              name: 'Replacement Belt',
              quantity: 2,
              unit: 'each',
              cost: 45.0,
              supplier: 'Fitness Parts Co.',
            },
            {
              id: 'mat1032',
              name: 'Lubricant (spray)',
              quantity: 1,
              unit: 'can',
              cost: 15.0,
              supplier: 'Hardware Store',
            },
          ],
          createdAt: '2025-08-07T10:00:00Z',
          updatedAt: '2025-08-08T16:00:00Z',
          notes: 'All equipment serviced and calibrated. Replaced worn belts on two treadmills.',
        },
        {
          id: 'WO104',
          title: 'Plumbing Leak Repair',
          description: 'Fixed leaking pipe in Unit 205 bathroom.',
          priority: 'high',
          status: 'completed',
          location: 'Building B - Unit 205',
          requestedBy: {
            name: 'Mike Thompson',
            unit: '205',
            contact: '(555) 234-5678',
            email: 'mike.thompson@example.com',
          },
          assignedTo: {
            vendorId: vendorId,
            vendorName: 'Current User',
          },
          scheduledDate: '2025-08-10T09:00:00Z',
          completedDate: '2025-08-10T12:30:00Z',
          estimatedHours: 2,
          actualHours: 3.5,
          materials: [
            {
              id: 'mat1041',
              name: 'Pipe Section (1in)',
              quantity: 1,
              unit: 'each',
              cost: 75.0,
              supplier: 'Plumbing Supply Co.',
            },
            {
              id: 'mat1042',
              name: 'Pipe Sealant',
              quantity: 1,
              unit: 'tube',
              cost: 8.5,
              supplier: 'Hardware Store',
            },
          ],
          createdAt: '2025-08-09T14:00:00Z',
          updatedAt: '2025-08-10T12:30:00Z',
          notes:
            'Replaced main pipe section and checked all connections. No further issues detected.',
        },
      ];
    }

    const allOrders = [...(this.mockWorkOrdersCache || [])];

    let filteredOrders = allOrders;

    if (filters) {
      if (filters.status) {
        filteredOrders = filteredOrders.filter((order) => filters.status!.includes(order.status));
      }
      if (filters.priority) {
        filteredOrders = filteredOrders.filter((order) =>
          filters.priority!.includes(order.priority),
        );
      }
      // Add more filter logic as needed
    }

    return filteredOrders;
  }

  private getMockWorkOrderById(workOrderId: string): WorkOrder {
    const mockOrders = this.getMockWorkOrders('vendor-001');
    const order = mockOrders.find((wo) => wo.id === workOrderId);

    if (!order) {
      throw new Error(`Work order with ID ${workOrderId} not found`);
    }

    return order;
  }

  private getMockTimeEntries(workOrderId: string): TimeEntry[] {
    if (!this.mockTimeEntriesCache[workOrderId]) {
      this.mockTimeEntriesCache[workOrderId] = [
        {
          id: 'te001',
          workOrderId: workOrderId,
          startTime: '2025-08-15T09:00:00Z',
          endTime: '2025-08-15T11:30:00Z',
          duration: 150, // 2.5 hours
          description: 'Diagnosed HVAC issue and ordered replacement parts',
          billable: true,
          rate: 85,
        },
        {
          id: 'te002',
          workOrderId: workOrderId,
          startTime: '2025-08-15T13:00:00Z',
          endTime: '2025-08-15T15:00:00Z',
          duration: 120, // 2 hours
          description: 'Installed new compressor unit',
          billable: true,
          rate: 85,
        },
      ];
    }
    return [...this.mockTimeEntriesCache[workOrderId]]; // return copy
  }

  private mockUpdateWorkOrderStatus(data: UpdateWorkOrderStatusData): WorkOrder {
    const mockOrder = this.getMockWorkOrderById(data.workOrderId);
    const updated: WorkOrder = {
      ...mockOrder,
      status: data.status,
      updatedAt: new Date().toISOString(),
      ...(data.status === 'completed' && { completedDate: new Date().toISOString() }),
      ...(data.status === 'in_progress' &&
        !mockOrder.actualHours && { actualHours: mockOrder.actualHours ?? 0 }),
    };
    // Persist back to cache
    if (this.mockWorkOrdersCache) {
      this.mockWorkOrdersCache = this.mockWorkOrdersCache.map((o) =>
        o.id === updated.id ? updated : o,
      );
    }
    return updated;
  }

  private mockCreateTimeEntry(data: CreateTimeEntryData): TimeEntry {
    const duration = data.endTime
      ? Math.floor(
          (new Date(data.endTime).getTime() - new Date(data.startTime).getTime()) / (1000 * 60),
        )
      : undefined;
    const entry: TimeEntry = {
      id: `te${Date.now()}`,
      workOrderId: data.workOrderId,
      startTime: data.startTime,
      endTime: data.endTime,
      duration,
      description: data.description,
      billable: data.billable || true,
      rate: data.rate || 75,
    };
    if (!this.mockTimeEntriesCache[data.workOrderId]) {
      this.mockTimeEntriesCache[data.workOrderId] = [];
    }
    // Add to start of list (most recent first)
    this.mockTimeEntriesCache[data.workOrderId].unshift(entry);
    return entry;
  }

  private mockUpdateTimeEntry(data: UpdateTimeEntryData): TimeEntry {
    // Locate entry across all work orders
    let found: TimeEntry | undefined;
    let parentWorkOrderId: string | undefined;
    for (const [woId, entries] of Object.entries(this.mockTimeEntriesCache)) {
      const idx = entries.findIndex((e) => e.id === data.entryId);
      if (idx !== -1) {
        found = entries[idx];
        parentWorkOrderId = woId;
        break;
      }
    }
    // If not found, create placeholder (shouldn't usually happen)
    if (!found) {
      found = {
        id: data.entryId,
        workOrderId: parentWorkOrderId || 'UNKNOWN',
        startTime: data.startTime || new Date().toISOString(),
        description: data.description || 'Updated entry',
        billable: true,
      } as TimeEntry;
      if (parentWorkOrderId) {
        this.mockTimeEntriesCache[parentWorkOrderId].unshift(found);
      }
    }

    const startTime = data.startTime || found.startTime;
    const endTime = data.endTime !== undefined ? data.endTime : found.endTime;
    // If we have both times and no explicit duration provided, compute minutes
    const duration =
      data.duration !== undefined
        ? data.duration
        : endTime && !found.endTime
          ? Math.floor((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000)
          : endTime
            ? Math.floor((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000)
            : found.duration;

    const updated: TimeEntry = {
      ...found,
      startTime,
      endTime,
      duration,
      description: data.description !== undefined ? data.description : found.description,
      billable: data.billable !== undefined ? data.billable : found.billable,
      rate: data.rate !== undefined ? data.rate : found.rate,
    };

    if (parentWorkOrderId) {
      this.mockTimeEntriesCache[parentWorkOrderId] = this.mockTimeEntriesCache[
        parentWorkOrderId
      ].map((e) => (e.id === updated.id ? updated : e));
    }
    return updated;
  }

  private mockDeleteTimeEntry(entryId: string) {
    for (const woId of Object.keys(this.mockTimeEntriesCache)) {
      const beforeLen = this.mockTimeEntriesCache[woId].length;
      this.mockTimeEntriesCache[woId] = this.mockTimeEntriesCache[woId].filter(
        (e) => e.id !== entryId,
      );
      if (this.mockTimeEntriesCache[woId].length !== beforeLen) {
        break; // deleted
      }
    }
  }

  private mockAddMaterial(data: AddMaterialData): Material {
    return {
      id: `mat${Date.now()}`,
      name: data.name,
      quantity: data.quantity,
      unit: data.unit,
      cost: data.unitCost,
      supplier: data.supplier,
    };
  }

  private getMockWorkOrderUpdates(workOrderId: string): WorkOrderUpdate[] {
    if (!this.mockWorkOrderUpdatesCache[workOrderId]) {
      this.mockWorkOrderUpdatesCache[workOrderId] = [
        {
          id: `u${Date.now()}seed`,
          timestamp: new Date().toISOString(),
          updatedBy: 'system',
          updateType: 'status',
          title: 'Work Order Created',
          description: 'Work order initialized in system',
          notifyResident: false,
        },
      ];
    }
    return [...this.mockWorkOrderUpdatesCache[workOrderId]];
  }

  private mockAddWorkOrderUpdate(data: CreateWorkOrderUpdateData): WorkOrderUpdate {
    if (!this.mockWorkOrderUpdatesCache[data.workOrderId]) {
      this.getMockWorkOrderUpdates(data.workOrderId);
    }
    const photos = data.photos?.map((file, idx) => ({
      id: `p${Date.now()}${idx}`,
      url: URL.createObjectURL(file),
      timestamp: new Date().toISOString(),
      takenBy: 'currentUser',
      type: 'during' as const,
      caption: file.name,
    }));
    const update: WorkOrderUpdate = {
      id: `u${Date.now()}`,
      timestamp: new Date().toISOString(),
      updatedBy: 'currentUser',
      updateType: data.updateType,
      title: data.title,
      description: data.description,
      notifyResident: !!data.notifyResident,
      ...(photos && photos.length ? { photos } : {}),
    };
    this.mockWorkOrderUpdatesCache[data.workOrderId].unshift(update); // newest first
    return update;
  }
}

export const vendorWorkOrderService = new VendorWorkOrderService();
